import { SCHEMA_V4_STORES, type StoreDefinition } from '../schema'
import type { Migration, MigrationContext } from './index'

/**
 * Version 4 — the materialised training week (D-105 §12).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE SMALLEST MIGRATION THIS PROJECT HAS WRITTEN, AND DELIBERATELY SO
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  One store is created and **not one record is read or written.** v3 was
 *  additive but still backfilled a domain goal per project; this one has nothing
 *  to backfill, and that is a decision rather than an omission:
 *
 *  - **No historical weeks are manufactured.** A week the user never saw must not
 *    be invented so that it can be reported as empty (D-105 §6).
 *  - **No schedule is derived from an existing `weeklyCommitment`.** Inventing
 *    the weekday an existing commitment happens on is D-101 §3's forbidden
 *    column — a number from nowhere, presented as the user's own plan.
 *
 *  So an existing install upgrades to v4 and looks exactly as it did, until the
 *  user creates a schedule themselves.
 *
 *  The v2 and v3 discipline still applies. **Nothing is awaited** — `run` is
 *  synchronous by signature (D-028) — and **nothing is repaired by hand**: if
 *  `createObjectStore` throws, the exception aborts the `versionchange`
 *  transaction and IndexedDB rolls back atomically with the stored version left
 *  at 3.
 */
export const migrationV4: Migration = {
  to: 4,
  describe: 'Add the training-week store for the recurring schedule',
  run({ database }: MigrationContext): void {
    for (const definition of SCHEMA_V4_STORES) {
      createStore(database, definition)
    }
  },
}

function createStore(database: IDBDatabase, definition: StoreDefinition): void {
  if (database.objectStoreNames.contains(definition.name)) return

  const store = database.createObjectStore(definition.name, { keyPath: definition.keyPath })
  for (const index of definition.indexes) {
    /* A compound index needs the array form passed through unchanged; the cast
       covers both shapes because `createIndex` accepts either. */
    store.createIndex(index.name, index.keyPath as string | string[])
  }
}
